import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, ExternalLink, FileText, Play, Download, BookOpen, Monitor, Globe } from 'lucide-react';
import { EditModeProvider, AdminToolbar } from '../components/EditableContent';
import CMSBlockRenderer from '../components/CMSBlockRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  datasheet: FileText,
  manual: BookOpen,
  video: Play,
  guide: BookOpen,
  download: Download,
  tool: Monitor,
  link: ExternalLink,
};

function VendorDetailPage() {
  const { slug } = useParams();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');

  useEffect(() => {
    axios.get(`${API}/vendors/${slug}`)
      .then(res => setVendor(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Header />
        <main className="max-w-6xl mx-auto px-4 py-12">
          <div className="h-48 bg-zinc-900 rounded-xl animate-pulse mb-8" />
          <div className="h-96 bg-zinc-900 rounded-xl animate-pulse" />
        </main>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Header />
        <main className="max-w-6xl mx-auto px-4 py-24 text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Vendor Not Found</h1>
          <Link to="/vendors" className="text-[#78BE20] hover:underline">Back to Vendors</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const videos = (vendor.resources || []).filter(r => r.type === 'video');
  const downloads = (vendor.resources || []).filter(r => r.type !== 'video');
  const hasVideos = videos.length > 0;
  const hasDownloads = downloads.length > 0;

  const tabs = [
    { id: 'about', label: 'About' },
    ...(hasVideos ? [{ id: 'videos', label: `Videos (${videos.length})` }] : []),
    ...(hasDownloads ? [{ id: 'downloads', label: `Downloads & Docs (${downloads.length})` }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="vendor-detail-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb */}
        <Link to="/vendors" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-[#78BE20] mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Vendors
        </Link>

        {/* Vendor Header */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-8 sm:p-10 mb-10">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-5 mb-4">
                {vendor.logo_url && (
                  <div className="h-10 max-w-[180px] shrink-0">
                    <img
                      src={vendor.logo_url}
                      alt={vendor.name}
                      className="h-full w-auto object-contain"
                      onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                    />
                  </div>
                )}
                <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">{vendor.name}</h1>
              </div>
              <p className="text-zinc-400 mt-3 leading-relaxed max-w-3xl">{vendor.description}</p>

              {/* Key stats */}
              {vendor.highlights && vendor.highlights.length > 0 && (
                <div className="flex flex-wrap gap-4 mt-6">
                  {vendor.highlights.map((h, i) => (
                    <div key={i} className="bg-zinc-800/80 border border-zinc-700 rounded-lg px-4 py-2 text-center min-w-[100px]">
                      <div className="text-lg font-bold text-[#78BE20]">{h.value}</div>
                      <div className="text-[11px] text-zinc-500 uppercase tracking-wider">{h.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {vendor.website && (
                <a href={vendor.website} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:text-[#78BE20] hover:border-[#78BE20]/50 w-full">
                    <Globe className="h-4 w-4 mr-2" /> Visit Website
                  </Button>
                </a>
              )}
              <Link to="/contact">
                <Button className="bg-[#78BE20] text-black hover:bg-[#65a31b] w-full">
                  Request a Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Tabs */}
        {tabs.length > 1 && (
          <div className="flex gap-1 mb-8 border-b border-zinc-800">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-3 text-sm font-medium transition-all border-b-2 -mb-[1px] ${
                  activeTab === tab.id
                    ? 'border-[#78BE20] text-[#78BE20]'
                    : 'border-transparent text-zinc-500 hover:text-zinc-300'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* About Tab */}
        {activeTab === 'about' && (
          <div className="space-y-8">
            {/* About Content */}
            {vendor.about_html ? (
              <div className="prose prose-invert max-w-none bg-zinc-900/40 border border-zinc-800 rounded-xl p-8"
                dangerouslySetInnerHTML={{ __html: vendor.about_html }} />
            ) : vendor.about_text ? (
              <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-8">
                {vendor.about_text.split('\n\n').map((para, i) => (
                  <p key={i} className="text-zinc-300 leading-relaxed mb-4 last:mb-0">{para}</p>
                ))}
              </div>
            ) : null}

            {/* Key Features */}
            {vendor.features && vendor.features.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-white mb-4">Why {vendor.name}?</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {vendor.features.map((f, i) => (
                    <div key={i} className="flex gap-3 p-5 bg-zinc-900/60 border border-zinc-800 rounded-xl">
                      <div className="w-2 h-2 rounded-full bg-[#78BE20] shrink-0 mt-2" />
                      <div>
                        <h3 className="text-white font-semibold text-sm">{f.title}</h3>
                        <p className="text-zinc-400 text-sm mt-1">{f.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <div className="grid md:grid-cols-2 gap-6">
            {videos.map((video, i) => (
              <div key={i} className="bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden">
                {video.url.includes('youtube.com') || video.url.includes('youtu.be') ? (
                  <div className="aspect-video">
                    <iframe
                      src={video.url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      title={video.title}
                      className="w-full h-full"
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    />
                  </div>
                ) : (
                  <a href={video.url} target="_blank" rel="noopener noreferrer" className="block p-6 hover:bg-zinc-800/40 transition-colors">
                    <Play className="h-10 w-10 text-[#78BE20] mb-3" />
                  </a>
                )}
                <div className="p-4">
                  <h3 className="text-white font-semibold">{video.title}</h3>
                  {video.description && <p className="text-zinc-500 text-sm mt-1">{video.description}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Downloads Tab */}
        {activeTab === 'downloads' && (
          <div className="space-y-3">
            {downloads.map((res, i) => {
              const Icon = iconMap[res.type] || FileText;
              return (
                <a
                  key={i}
                  href={res.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-zinc-900/60 border border-zinc-800 rounded-xl hover:border-[#78BE20]/40 transition-all group"
                  data-testid={`resource-${i}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#78BE20]/10 flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-[#78BE20]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium text-sm group-hover:text-[#78BE20] transition-colors">{res.title}</h3>
                    {res.description && <p className="text-zinc-500 text-xs mt-0.5">{res.description}</p>}
                  </div>
                  <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-[10px] uppercase shrink-0">
                    {res.type}
                  </Badge>
                  <ExternalLink className="h-4 w-4 text-zinc-600 group-hover:text-[#78BE20] transition-colors shrink-0" />
                </a>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <section className="mt-16 bg-gradient-to-r from-[#78BE20]/10 to-zinc-900/80 border border-[#78BE20]/20 rounded-2xl p-8 sm:p-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Interested in {vendor.name} products?</h2>
          <p className="text-zinc-400 mb-6 max-w-lg mx-auto">
            Contact our team for pricing, availability, and expert advice on deploying {vendor.name} solutions.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-[#78BE20] text-black hover:bg-[#65a31b] h-12 px-8 text-base">Get in Touch</Button>
            </Link>
            <Link to="/products">
              <Button variant="outline" className="border-zinc-700 text-zinc-300 h-12 px-8 text-base">Browse Products</Button>
            </Link>
          </div>
        </section>
      </main>
      <CMSBlockRenderer excludeTypes={['hero']} />
      <Footer />
      <AdminToolbar />
    </div>
  );
}

function VendorDetailWrapper() {
  return (
    <EditModeProvider pageSlug="vendors">
      <VendorDetailPage />
    </EditModeProvider>
  );
}

export default VendorDetailWrapper;
